import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Carousel, CarouselItem } from '@americanexpress/dls-react';
import { getLocaleData } from '@/selectors/localeSelectors';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import * as loungeHelper from '@/helpers/loungeHelper';
import { useRouteParams } from '@/context/routeParamsContext';
import { cn } from '@/helpers/utils';
import { getOneCmsLoungeFinderImagesBaseUrl } from '@/selectors/oneCmsSelectors';
import LFLink from '../common/LFLink';

const SearchLoungeResult = ({ lounge }) => {
  const { name, id } = lounge;

  const {
    lounges: { open: openLabel, closed: closedLabel, opensAt, closesAt },
    config: {
      dateFormatting: { timeFormat },
    },
  } = useCommonContent();
  const { product } = useRouteParams();
  const { locale } = useSelector(getLocaleData);

  const oneCmsBaseImgUrl = useSelector(getOneCmsLoungeFinderImagesBaseUrl);
  const placeholderImgUrl = `${oneCmsBaseImgUrl}placeholder.jpg`;
  const isLoungeOpen = loungeHelper.isLoungeOpen(locale, lounge);
  const isLoungeTempClosed = loungeHelper.isLoungeTempClosed(lounge);
  const loungeHours = loungeHelper.generateLoungeHoursAsString(
    locale,
    lounge,
    timeFormat
  );

  const loungeTerminal = lounge.location?.terminal;
  const loungePath = loungeHelper.generateLoungeUrl(
    lounge.name,
    loungeTerminal,
    lounge.id
  );

  const loungeImages = lounge.images?.length
    ? lounge.images
    : [{ images: { medium: placeholderImgUrl, public_id: 'placeholder' } }];

  const openingTime = loungeHours?.split(' - ')?.[0];
  const closingTime = loungeHours?.split(' - ')?.[1];

  const loungeOperatingStartStopTime = () => {
    if (isLoungeTempClosed) return null;

    return isLoungeOpen ? (
      <span className="tw-font-normal">
        &nbsp;• {closesAt} {closingTime}
      </span>
    ) : (
      <span className="tw-font-normal">
        &nbsp;• {opensAt} {openingTime}
      </span>
    );
  };

  return (
    <LFLink
      className="tw-w-full tw-self-stretch"
      to={`${product}/${lounge.airport.iata}/${loungePath}`}
      key={`lounge-result-link-${id}`}
      detail={`lounge-result-link-${id}`}
      element="lounge-result-link"
    >
      <div
        className={cn(
          'tw-group tw-h-full tw-rounded-2xl tw-border tw-border-gray-300 tw-p-4 tw-shadow-low',
          'focus-within_tw-shadow-md hover_tw-shadow-md',
          'tw-transition-shadow tw-duration-200 hover_tw-duration-300'
        )}
      >
        <Carousel
          isNavigationOnSlides
          isSlideFullWidth
          label="Lounge images"
          className={cn(
            'carousel-override first-child_tw-rounded-lg',
            'no-touch_last-child_first-child_tw-opacity-0 no-touch_last-child_first-child_focus-within_tw-opacity-100 no-touch_last-child_first-child_group-hover_tw-opacity-100 no-touch_last-child_first-child_group-focus_tw-opacity-100',
            'no-touch_last-child_first-child_tw-duration-200 no-touch_last-child_first-child_group-hover_tw-duration-300'
          )}
          onClick={(e) => {
            if (e.target.tagName === 'IMG') return; // don't block image click through

            // don't navigate to lounge detail page when clicking on the carousel nav
            e.preventDefault();
          }}
        >
          {loungeImages.map(({ images, public_id }, i) => (
            <CarouselItem
              key={images.public_id}
              id={public_id}
              className="tw-w-full tw-flex-shrink-0"
            >
              <img
                src={images.medium}
                alt={`Lounge thumbnail ${i + 1}`}
                width={300}
                height={200}
                className="tw-aspect-image tw-w-full tw-object-cover"
              />
            </CarouselItem>
          ))}
        </Carousel>
        <div className="tw-mt-4 tw-min-h-26 tw-text-left">
          <h2 className="tw-heading-sm-bold tw-text-gray-900">{name}</h2>
          <div className="tw-body-sm tw-mt-1 tw-text-gray-800">
            <p>{loungeTerminal}</p>
            <div id={`lounge-status-${id}`}>
              {' '}
              {isLoungeOpen === null ? null : isLoungeOpen ? (
                <span className="tw-capitalize tw-text-status-success">
                  {openLabel.toLocaleLowerCase()}
                </span>
              ) : (
                <span className="tw-capitalize tw-text-status-caution">
                  {closedLabel.toLocaleLowerCase()}
                </span>
              )}
              {loungeOperatingStartStopTime()}
            </div>
          </div>
        </div>
      </div>
    </LFLink>
  );
};

SearchLoungeResult.propTypes = {
  lounge: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    airport: PropTypes.shape({
      iata: PropTypes.string,
    }),
    location: PropTypes.shape({
      terminal: PropTypes.string,
    }),
    images: PropTypes.arrayOf(
      PropTypes.shape({
        images: PropTypes.shape({ medium: PropTypes.string }),
        public_id: PropTypes.string,
      })
    ),
  }),
};

export default SearchLoungeResult;
